/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { MyShowsService } from "@tivocorp/data-domain-lib";
import type {
  MyHubCarousel,
  MyHubFilterList,
  MyHubFolderDetail,
  StorageInfo
} from "client-code/myshows";
import type {
  TMyHubCarouselListPage,
  TMyHubFetchCarouselParams,
  TMyHubFetchCarouselsParams,
  TMyHubFetchFolderParams
} from "@tivocorp/data-domain-lib";

import type { IMyShowsInteractor } from "../../interfaces";
import type { TNetworkOptions } from "../../types";
import { Log } from "../../logging";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";

/**
 * Facade for MyShows (DVR) operations, providing a clean API for consumers.
 *
 * Service instances are **not** stored as class members — a fresh instance is
 * created per call so the interactor holds no mutable state, matching
 * {@link RecordingsStatusInteractor}.
 */
export class MyShowsInteractor
  extends BaseInteractor
  implements IMyShowsInteractor
{
  private static readonly TAG: string = "MyShowsInteractor";

  /**
   * Fetches the subscriber's DVR storage figures.
   * API: GET /v1/diskSpace (cloudcore-myshows).
   */
  public async fetchDiskSpace(options?: TNetworkOptions): Promise<StorageInfo> {
    return this.executeGuarded(
      "fetchDiskSpace",
      async (guardedSignal: AbortSignal): Promise<StorageInfo> => {
        Log.d(MyShowsInteractor.TAG, "fetchDiskSpace() called");
        const params = await this.buildServicesParams(
          TIVO_SERVICES.MyShowsService,
          ApplicationFeatureArea.MY_SHOWS
        );
        const service: MyShowsService = new MyShowsService(params);
        return service.fetchDiskSpace({}, guardedSignal);
      },
      options
    );
  }

  /**
   * Fetches My Hub carousels for a given filter/tab.
   * API: GET /v1/myHub/carousels (cloudcore-myshows).
   */
  public async fetchMyHubCarousels(
    params: TMyHubFetchCarouselsParams,
    options?: TNetworkOptions
  ): Promise<TMyHubCarouselListPage> {
    return this.executeGuarded(
      "fetchMyHubCarousels",
      async (guardedSignal: AbortSignal): Promise<TMyHubCarouselListPage> => {
        Log.d(MyShowsInteractor.TAG, "fetchMyHubCarousels() called");
        const servicesParams = await this.buildServicesParams(
          TIVO_SERVICES.MyShowsService,
          ApplicationFeatureArea.MY_SHOWS
        );
        const service: MyShowsService = new MyShowsService(servicesParams);
        return service.fetchMyHubCarousels(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Pages the tiles of a single My Hub carousel (row) past the initial slice
   * returned by fetchMyHubCarousels.
   * API: GET /v1/myHub/carousels/{carouselId} (cloudcore-myshows).
   */
  public async fetchMyHubCarousel(
    params: TMyHubFetchCarouselParams,
    options?: TNetworkOptions
  ): Promise<MyHubCarousel> {
    return this.executeGuarded(
      "fetchMyHubCarousel",
      async (guardedSignal: AbortSignal): Promise<MyHubCarousel> => {
        Log.d(MyShowsInteractor.TAG, "fetchMyHubCarousel() called");
        const servicesParams = await this.buildServicesParams(
          TIVO_SERVICES.MyShowsService,
          ApplicationFeatureArea.MY_SHOWS
        );
        const service: MyShowsService = new MyShowsService(servicesParams);
        return service.fetchMyHubCarousel(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Fetches collection/folder detail for the manage-series screen.
   * API: GET /v1/myHub/myHubFolder/unifiedCollection/{unifiedCollectionId}
   * (cloudcore-myshows).
   */
  public async fetchMyHubFolder(
    params: TMyHubFetchFolderParams,
    options?: TNetworkOptions
  ): Promise<MyHubFolderDetail> {
    return this.executeGuarded(
      "fetchMyHubFolder",
      async (guardedSignal: AbortSignal): Promise<MyHubFolderDetail> => {
        Log.d(MyShowsInteractor.TAG, "fetchMyHubFolder() called");
        const servicesParams = await this.buildServicesParams(
          TIVO_SERVICES.MyShowsService,
          ApplicationFeatureArea.MY_SHOWS
        );
        const service: MyShowsService = new MyShowsService(servicesParams);
        return service.fetchMyHubFolder(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Fetches the My Hub filter/tab strip.
   * API: GET /v1/myHub/myHubFilters (cloudcore-myshows).
   */
  public async fetchMyHubFilters(
    options?: TNetworkOptions
  ): Promise<MyHubFilterList> {
    return this.executeGuarded(
      "fetchMyHubFilters",
      async (guardedSignal: AbortSignal): Promise<MyHubFilterList> => {
        Log.d(MyShowsInteractor.TAG, "fetchMyHubFilters() called");
        const servicesParams = await this.buildServicesParams(
          TIVO_SERVICES.MyShowsService,
          ApplicationFeatureArea.MY_SHOWS
        );
        const service: MyShowsService = new MyShowsService(servicesParams);
        return service.fetchMyHubFilters({}, guardedSignal);
      },
      options
    );
  }

  /**
   * Permanently deletes every recording on the Deleted tab. Unscoped — no id
   * to pass, it acts on the current subscriber's whole Deleted list.
   * API: DELETE /v1/myhub/delete/permanent (cloudcore-myshows).
   */
  public async deleteMyHubPermanent(options?: TNetworkOptions): Promise<void> {
    return this.executeGuarded(
      "deleteMyHubPermanent",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(MyShowsInteractor.TAG, "deleteMyHubPermanent() called");
        const servicesParams = await this.buildServicesParams(
          TIVO_SERVICES.MyShowsService,
          ApplicationFeatureArea.MY_SHOWS
        );
        const service: MyShowsService = new MyShowsService(servicesParams);
        return service.deleteMyHubPermanent({}, guardedSignal);
      },
      options
    );
  }
}
